<template>
  <base-layout>
    <div class="col-xs-12 main" slot="main">
      <div class="row ls-charts book-appointment">
        <h1>Book Appointment</h1>

        <!-- Booked appointment -->
        <div v-if="bookedAppointment" class="col-xs-12">
          <div class="booking-card confirmation-card">
            <div class="confirmation-header">
              <i class="fa fa-check-circle"></i>
              <div>
                <h2>Appointment reserved</h2>
                <p><strong>{{ bookedCampaign.owner_name }}</strong> <span class="muted">/</span> {{ bookedCampaign.name }}</p>
              </div>
            </div>

            <div class="expiry-warning">
              <i class="fa fa-hourglass-half"></i>
              <span>Send this appointment within <strong>{{ bookedAppointment.blocked_period }} minutes</strong> or it will expire.</span>
            </div>

            <div class="appointment-id">
              <span class="field-label">Appointment ID</span>
              <div class="appointment-id-value">
                <code>{{ bookedAppointment.id }}</code>
                <button type="button" class="btn btn-sm" :class="copyFeedback ? 'btn-success' : 'btn-info'" @click="copyAppointmentId">
                  <i class="fa" :class="copyFeedback ? 'fa-check' : 'fa-copy'"></i> {{ copyFeedback ? 'Copied!' : 'Copy' }}
                </button>
              </div>
            </div>

            <dl class="appointment-details">
              <div>
                <dt>When</dt>
                <dd>{{ bookedAppointmentStartTime }}</dd>
              </div>
              <div>
                <dt>Type</dt>
                <dd>
                  <i class="fa fa-fw" :class="[appointmentType(bookedAppointment.appointment_type).icon, 'type-color-' + bookedAppointment.appointment_type]"></i>
                  {{ appointmentType(bookedAppointment.appointment_type).label }}
                </dd>
              </div>
              <div v-if="bookedAppointment.appointment_type == 'in-person-at-office'">
                <dt>Office address</dt>
                <dd>{{ bookedAppointment.appointment_location }}</dd>
              </div>
              <div>
                <dt>Company</dt>
                <dd>{{ bookedAppointment.agent.agency }}</dd>
              </div>
              <div>
                <dt>Agent</dt>
                <dd>{{ bookedAppointment.agent.name }}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd><a :href="'mailto:' + bookedAppointment.agent.email">{{ bookedAppointment.agent.email }}</a></dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{{ bookedAppointment.agent.phone }}</dd>
              </div>
            </dl>

            <div class="confirmation-actions">
              <button type="button" class="btn btn-default cancel-button" :disabled="cancellingAppointment" @click="cancelAppointment">
                <i class="fa" :class="cancellingAppointment ? 'fa-spinner fa-spin' : 'fa-times'"></i> Cancel &amp; Back
              </button>
              <button v-if="appointmentIdCopied" type="button" class="btn btn-primary" @click="restart">
                <i class="fa fa-calendar-plus-o"></i> New Appointment
              </button>
              <span v-else class="muted">Copy the appointment ID to start a new one.</span>
            </div>
          </div>
        </div>

        <!-- Search & results -->
        <div v-else class="col-xs-12">
          <div class="booking-card search-card">
            <FormulateForm class="search-form" @submit="searchSlots">
              <FormulateInput
                v-model="zipcode"
                name="zipcode"
                type="text"
                label="Zip code"
                placeholder="e.g. 33101"
                maxlength="5"
                :error="findSlotsErrors.zipcode" />
              <FormulateInput
                v-model="desired_date"
                name="desired_date"
                type="date"
                label="Starting from"
                :error="findSlotsErrors.desired_date" />
              <div class="formulate-input search-action">
                <span class="formulate-input-label" aria-hidden="true">&nbsp;</span>
                <button type="submit" class="btn btn-primary" :disabled="loadingSlots">
                  <i class="fa" :class="loadingSlots ? 'fa-spinner fa-spin' : 'fa-search'"></i> Find Slots
                </button>
              </div>
            </FormulateForm>
            <p v-if="findSlotsErrors.non_field_errors" class="text-danger form-error">{{ findSlotsErrors.non_field_errors }}</p>
          </div>

          <div v-if="bookingError" class="alert alert-danger booking-error">
            <button type="button" class="close" aria-label="Close" @click="bookingError = null">&times;</button>
            <i class="fa fa-exclamation-circle"></i> {{ bookingError }}
          </div>

          <div v-if="loadingSlots" class="booking-card empty-state">
            <i class="fa fa-circle-o-notch fa-spin"></i>
            <p>Looking for available slots&hellip;</p>
          </div>

          <div v-else-if="!lastRequest" class="booking-card empty-state">
            <i class="fa fa-calendar"></i>
            <p class="empty-state-title">Find an appointment</p>
            <p>Enter a zip code to see the available slots across all your campaigns.</p>
          </div>

          <div v-else-if="!campaignSlots.length" class="booking-card empty-state">
            <i class="fa fa-calendar-times-o"></i>
            <p class="empty-state-title">No slots available</p>
            <p>No licensed agents are available in {{ lastRequest.zipcode }} for the selected dates.</p>
          </div>

          <div v-else class="results">
            <div class="results-header">
              <span>
                <strong>{{ campaignSlots.length }}</strong> {{ campaignSlots.length === 1 ? 'campaign' : 'campaigns' }}
                with availability in <strong>{{ lastRequest.zipcode }}</strong>
              </span>
              <span class="results-timezone"><i class="fa fa-clock-o"></i> Times shown in <strong>{{ timezoneLabel }}</strong></span>
            </div>

            <div v-for="entry in campaignSlots"
                 :key="entry.campaign.id"
                 class="campaign-card"
                 :class="{'is-expanded': isExpanded(entry), 'has-selection': isCampaignSelected(entry)}">
              <button type="button"
                      class="campaign-header"
                      :aria-expanded="isExpanded(entry) ? 'true' : 'false'"
                      @click="toggleCampaign(entry)">
                <i class="fa fa-chevron-right campaign-chevron"></i>
                <span class="campaign-title">
                  <span class="campaign-owner">{{ entry.campaign.owner_name }}</span>
                  <span class="campaign-separator">/</span>
                  <span class="campaign-name">{{ entry.campaign.name }}</span>
                </span>
                <span class="campaign-types">
                  <i v-for="type in entry.appointmentTypes"
                     :key="type"
                     class="fa fa-fw"
                     :class="[appointmentType(type).icon, 'type-color-' + type]"
                     :title="appointmentType(type).label"></i>
                </span>
                <span class="campaign-next">
                  <span class="field-label">Next available</span>
                  {{ nextSlotLabel(entry.nextSlot) }}
                </span>
                <span class="campaign-count">{{ entry.count }} {{ entry.count === 1 ? 'slot' : 'slots' }}</span>
              </button>

              <div v-show="isExpanded(entry)" class="campaign-body">
                <div v-for="day in entry.days" :key="day.key" class="slot-day">
                  <div class="slot-day-label">
                    <span class="slot-day-weekday">{{ day.weekday }}</span>
                    <span class="slot-day-date">{{ day.date }}</span>
                  </div>
                  <div class="slot-day-slots">
                    <div v-for="block in day.blocks" :key="block[0].start_timestamp" class="slot-block">
                      <div v-for="slot in block"
                           :key="slot.start_timestamp"
                           class="slot-card"
                           :class="{'is-selected': isSlotSelected(slot)}">
                        <div class="slot-time">{{ timeLabel(slot) }}</div>
                        <div class="slot-options">
                          <button v-for="(option, index) in slot.appointment_types"
                                  :key="index"
                                  type="button"
                                  class="slot-option"
                                  :class="['slot-option-' + option.appointment_type, {'is-selected': isOptionSelected(option)}]"
                                  :title="optionTitle(option)"
                                  @click="selectOption(entry, slot, option)">
                            <i class="fa" :class="appointmentType(option.appointment_type).icon"></i>
                            {{ option.location || appointmentType(option.appointment_type).shortLabel }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selection" class="selection-bar">
              <div class="selection-summary">
                <span class="selection-icon" :class="'type-background-' + selection.option.appointment_type">
                  <i class="fa" :class="appointmentType(selection.option.appointment_type).icon"></i>
                </span>
                <div>
                  <div class="selection-title">{{ optionTitle(selection.option) }}</div>
                  <div class="selection-subtitle">{{ selectionTimeLabel }} &middot; {{ selection.campaign.owner_name }} / {{ selection.campaign.name }}</div>
                </div>
              </div>
              <div class="selection-actions">
                <button type="button" class="btn btn-link" :disabled="confirmingSelection" @click="selection = null">Clear</button>
                <button type="button" class="btn btn-primary" :disabled="confirmingSelection" @click="bookAppointment">
                  <i class="fa" :class="confirmingSelection ? 'fa-spinner fa-spin' : 'fa-check'"></i> Confirm Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </base-layout>
</template>

<script>
  import {BaseLayout} from 'components/layouts';

  const APPOINTMENT_TYPES = {
    'phone-call': {label: 'Phone Call', shortLabel: 'Phone', icon: 'fa-phone'},
    'online': {label: 'Online Meeting', shortLabel: 'Online', icon: 'fa-video-camera'},
    'in-person-at-home': {label: "Beneficiary's Location", shortLabel: 'Home visit', icon: 'fa-home'},
    'in-person-at-office': {label: "Agent's Office", shortLabel: 'Office', icon: 'fa-building-o'},
  };

  export default {
    components: {
      BaseLayout,
    },
    data() {
      return {
        zipcode: null,
        desired_date: null,
        lastRequest: null,
        campaignSlots: [],
        expandedCampaignIds: [],
        loadingSlots: false,
        findSlotsErrors: {
          zipcode: null,
          desired_date: null,
          non_field_errors: null,
        },
        selection: null,
        confirmingSelection: false,
        bookingError: null,
        bookedAppointment: null,
        bookedCampaign: null,
        bookedTimezone: null,
        cancellingAppointment: false,
        copyFeedback: false,
        appointmentIdCopied: false,
      };
    },
    computed: {
      timezoneLabel() {
        let slot = this.campaignSlots.length ? this.campaignSlots[0].nextSlot : null;
        if (!slot) {
          return null;
        }

        let timezonePart = new Intl.DateTimeFormat('en-US', {timeZone: slot.timezone, timeZoneName: 'short'})
          .formatToParts(new Date(slot.start_timestamp))
          .find((part) => part.type === 'timeZoneName');
        return timezonePart ? timezonePart.value : slot.timezone;
      },
      selectionTimeLabel() {
        if (this.selection) {
          return this.formatTimestamp(this.selection.slot.start_timestamp, this.selection.slot.timezone,
                                      {weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'});
        }
      },
      bookedAppointmentStartTime() {
        if (this.bookedAppointment && this.bookedTimezone) {
          return this.formatTimestamp(this.bookedAppointment.start_timestamp, this.bookedTimezone,
                                      {weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'});
        }
      },
    },
    methods: {
      appointmentType(key) {
        return APPOINTMENT_TYPES[key] || {label: key, shortLabel: key, icon: 'fa-calendar'};
      },
      formatTimestamp(timestamp, timezone, options) {
        return new Date(timestamp).toLocaleString('en-US', Object.assign({timeZone: timezone}, options));
      },
      timeLabel(slot) {
        return this.formatTimestamp(slot.start_timestamp, slot.timezone, {hour: 'numeric', minute: '2-digit'});
      },
      nextSlotLabel(slot) {
        return this.formatTimestamp(slot.start_timestamp, slot.timezone,
                                    {weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'});
      },
      durationInMilliseconds(duration) {
        // DRF serializes durations as "HH:MM:SS"
        let [hours, minutes, seconds] = duration.split(':').map(Number);
        return ((hours * 60 + minutes) * 60 + seconds) * 1000;
      },
      optionTitle(option) {
        let label = this.appointmentType(option.appointment_type).label;
        return option.location ? label + ' @ ' + option.location : label;
      },
      buildCampaignSlots(entry) {
        let slots = entry.results.slice().sort((a, b) => new Date(a.start_timestamp) - new Date(b.start_timestamp));
        let days = [];
        let appointmentTypes = [];

        slots.forEach((slot) => {
          let dayKey = this.formatTimestamp(slot.start_timestamp, slot.timezone, {year: 'numeric', month: '2-digit', day: '2-digit'});
          let day = days[days.length - 1];

          if (!day || day.key !== dayKey) {
            day = {
              key: dayKey,
              weekday: this.formatTimestamp(slot.start_timestamp, slot.timezone, {weekday: 'short'}),
              date: this.formatTimestamp(slot.start_timestamp, slot.timezone, {month: 'short', day: 'numeric'}),
              blocks: [],
            };
            days.push(day);
          }

          // Consecutive slots share a block; a gap in the day starts a new one
          let block = day.blocks[day.blocks.length - 1];
          let previousSlot = block ? block[block.length - 1] : null;
          if (!previousSlot ||
              new Date(slot.start_timestamp) - new Date(previousSlot.start_timestamp) > this.durationInMilliseconds(previousSlot.duration)) {
            block = [];
            day.blocks.push(block);
          }
          block.push(slot);

          slot.appointment_types.forEach((option) => {
            if (appointmentTypes.indexOf(option.appointment_type) === -1) {
              appointmentTypes.push(option.appointment_type);
            }
          });
        });

        return {
          campaign: entry.campaign,
          count: entry.count,
          nextSlot: slots[0],
          days: days,
          appointmentTypes: appointmentTypes,
        };
      },
      isExpanded(entry) {
        return this.expandedCampaignIds.indexOf(entry.campaign.id) > -1;
      },
      toggleCampaign(entry) {
        let index = this.expandedCampaignIds.indexOf(entry.campaign.id);
        if (index > -1) {
          this.expandedCampaignIds.splice(index, 1);
        } else {
          this.expandedCampaignIds.push(entry.campaign.id);
        }
      },
      isCampaignSelected(entry) {
        return !!this.selection && this.selection.campaign.id === entry.campaign.id;
      },
      isSlotSelected(slot) {
        return !!this.selection && this.selection.slot === slot;
      },
      isOptionSelected(option) {
        return !!this.selection && this.selection.option === option;
      },
      selectOption(entry, slot, option) {
        this.selection = this.isOptionSelected(option) ? null : {campaign: entry.campaign, slot: slot, option: option};
      },
      clearErrors() {
        Object.keys(this.findSlotsErrors).forEach((key) => {
          this.findSlotsErrors[key] = null;
        });
      },
      searchSlots() {
        let request = {};
        if (this.zipcode) {
          request.zipcode = this.zipcode;
        }
        if (this.desired_date) {
          request.desired_date = this.desired_date;
        }

        this.bookingError = null;
        return this.findSlots(request);
      },
      async findSlots(request) {
        this.loadingSlots = true;
        this.selection = null;
        this.clearErrors();

        try {
          let response = await API.providerCampaigns.findAllSlots(request);
          this.campaignSlots = response.body
            .filter((entry) => entry.count > 0)
            .map((entry) => this.buildCampaignSlots(entry));

          // Keep open whatever was open; a single campaign is opened right away
          let campaignIds = this.campaignSlots.map((entry) => entry.campaign.id);
          this.expandedCampaignIds = campaignIds.length === 1
            ? campaignIds
            : this.expandedCampaignIds.filter((id) => campaignIds.indexOf(id) > -1);
          this.lastRequest = request;
        } catch (err) {
          this.handleErrorResponse(this.findSlotsErrors, err);
          this.campaignSlots = [];
          this.lastRequest = null;
        } finally {
          this.loadingSlots = false;
        }
      },
      async bookAppointment() {
        let {campaign, slot, option} = this.selection;
        this.confirmingSelection = true;
        this.bookingError = null;

        try {
          let response = await API.providerCampaigns.bookAppointment(campaign.id, {
            zipcode: this.lastRequest.zipcode,
            slot_options: option.slots,
            carrier_preference: slot.carrier_preference,
          });
          this.bookedAppointment = response.body;
          this.bookedCampaign = campaign;
          this.bookedTimezone = slot.timezone;
          this.selection = null;
        } catch (err) {
          await this.findSlots(this.lastRequest);
          this.bookingError = (err.body && err.body.non_field_errors)
            ? [].concat(err.body.non_field_errors).join(' ')
            : 'The appointment could not be booked. Please try again.';
        } finally {
          this.confirmingSelection = false;
        }
      },
      async cancelAppointment() {
        let ok = window.confirm('Are you sure you want to cancel this appointment? This action cannot be undone.');
        if (!ok) {
          return;
        }

        this.cancellingAppointment = true;
        try {
          await API.providerCampaigns.unblockAppointment(this.bookedAppointment.id);
          this.resetBookedAppointment();
          await this.findSlots(this.lastRequest);
        } finally {
          this.cancellingAppointment = false;
        }
      },
      handleErrorResponse(errorObj, errorResponse) {
        let body = errorResponse && errorResponse.body;
        if (!body || typeof body !== 'object') {
          errorObj.non_field_errors = 'Something went wrong. Please try again.';
          return;
        }

        for (const [key, errorMsgs] of Object.entries(body)) {
          let errorKey = key in errorObj ? key : 'non_field_errors';
          errorObj[errorKey] = [].concat(errorMsgs).join(' ');
        }
      },
      copyAppointmentId() {
        navigator.clipboard.writeText(this.bookedAppointment.id).then(() => {
          this.appointmentIdCopied = true;
          this.copyFeedback = true;
          setTimeout(() => {
            this.copyFeedback = false;
          }, 2000);
        });
      },
      resetBookedAppointment() {
        this.bookedAppointment = null;
        this.bookedCampaign = null;
        this.bookedTimezone = null;
        this.appointmentIdCopied = false;
        this.copyFeedback = false;
      },
      restart() {
        this.resetBookedAppointment();
        this.zipcode = null;
        this.desired_date = null;
        this.lastRequest = null;
        this.campaignSlots = [];
        this.expandedCampaignIds = [];
        this.bookingError = null;
        this.clearErrors();
      }
    }
  }
</script>

<style lang="scss">
  @import "./static/css/_variables.scss";

  $book-appointment-border-color: #e3e7eb;
  $book-appointment-heading-color: #34404b;
  $book-appointment-muted-color: #8b959e;
  $book-appointment-radius: 6px;
  $book-appointment-type-colors: (
    'phone-call': #3a9d5d,
    'online': #2aa0c2,
    'in-person-at-home': #e0902a,
    'in-person-at-office': $ls-action
  );

  @mixin book-appointment-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: $book-appointment-muted-color;
  }

  @keyframes book-appointment-reveal {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  .book-appointment {
    .muted {
      color: $book-appointment-muted-color;
    }

    .field-label {
      @include book-appointment-label;
    }

    .booking-card {
      margin-bottom: 16px;
      background: #fff;
      border: 1px solid $book-appointment-border-color;
      border-radius: $book-appointment-radius;
      box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
    }

    // Search

    .search-card {
      padding: 18px 20px 0;
    }

    .search-form {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      gap: 0 16px;

      .formulate-input {
        flex: 0 1 220px;
        margin-bottom: 18px;
      }
    }

    .search-form .search-action {
      flex: 0 0 auto;

      .btn {
        min-width: 130px;
        padding-top: 9px;
        padding-bottom: 9px;
      }
    }

    .form-error {
      margin-top: -8px;
      padding-bottom: 12px;
    }

    .booking-error {
      border-radius: $book-appointment-radius;
    }

    // Empty, loading and initial states

    .empty-state {
      padding: 48px 20px;
      text-align: center;
      color: $book-appointment-muted-color;

      > .fa {
        margin-bottom: 12px;
        font-size: 36px;
        color: lighten($book-appointment-muted-color, 15%);
      }

      p {
        margin: 0 0 4px;
      }

      .empty-state-title {
        font-size: 17px;
        font-weight: 700;
        color: $book-appointment-heading-color;
      }
    }

    // Campaigns

    .results-header {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: baseline;
      gap: 4px 16px;
      padding: 4px 2px 10px;
      color: $book-appointment-heading-color;
    }

    .results-timezone {
      padding: 3px 10px;
      background: #fff;
      border: 1px solid $book-appointment-border-color;
      border-radius: 12px;
      font-size: 13px;
      color: $book-appointment-heading-color;
    }

    .campaign-card {
      margin-bottom: 10px;
      background: #fff;
      border: 1px solid $book-appointment-border-color;
      border-radius: $book-appointment-radius;
      transition: border-color .2s;

      &:hover {
        border-color: darken($book-appointment-border-color, 10%);
      }

      &.has-selection {
        border-color: $ls-highlight-color;
      }
    }

    .campaign-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 24px;
      width: 100%;
      padding: 14px 18px;
      background: transparent;
      border: 0;
      border-radius: $book-appointment-radius;
      text-align: left;
      color: inherit;
      cursor: pointer;

      &:focus {
        outline: none;
      }

      &:focus-visible {
        box-shadow: inset 0 0 0 2px rgba($ls-action, .35);
      }
    }

    .campaign-chevron {
      color: $book-appointment-muted-color;
      transition: transform .2s;
    }

    .is-expanded .campaign-chevron {
      transform: rotate(90deg);
    }

    .campaign-title {
      flex: 1 1 200px;
      min-width: 0;
      font-size: 16px;
      color: $book-appointment-heading-color;
    }

    .campaign-owner {
      font-weight: 700;
    }

    .campaign-separator {
      margin: 0 4px;
      color: $book-appointment-muted-color;
    }

    .campaign-types {
      font-size: 15px;
      white-space: nowrap;
    }

    .campaign-next {
      font-size: 13px;
      white-space: nowrap;
      color: $book-appointment-heading-color;
    }

    .campaign-count {
      padding: 3px 10px;
      border-radius: 12px;
      background: rgba($ls-action, .1);
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
      color: $ls-action;
    }

    .campaign-body {
      padding: 4px 18px 10px 46px;
      border-top: 1px solid $book-appointment-border-color;
      animation: book-appointment-reveal .2s ease-out;
    }

    // Slots

    .slot-day {
      display: flex;
      gap: 16px;
      padding: 12px 0;

      & + .slot-day {
        border-top: 1px dashed darken($book-appointment-border-color, 15%);
      }
    }

    .slot-day-label {
      flex: 0 0 64px;
      padding-top: 6px;
      line-height: 1.25;
    }

    .slot-day-weekday {
      display: block;
      font-weight: 700;
      color: $book-appointment-heading-color;
    }

    .slot-day-date {
      display: block;
      font-size: 12px;
      color: $book-appointment-heading-color;
    }

    .slot-day-slots {
      display: flex;
      flex: 1 1 auto;
      flex-wrap: wrap;
      gap: 20px 32px;
    }

    .slot-block {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .slot-card {
      padding: 6px 8px 8px;
      background: $ls-background-light-color;
      border: 1px solid $book-appointment-border-color;
      border-radius: $book-appointment-radius;
      transition: border-color .15s, box-shadow .15s;

      &.is-selected {
        background: #fff8f8;
        border-color: $ls-highlight-color;
        box-shadow: 0 0 0 2px rgba($ls-highlight-color, .2);
      }
    }

    .slot-time {
      margin-bottom: 5px;
      font-size: 13px;
      font-weight: 700;
      color: $book-appointment-heading-color;
    }

    .slot-options {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .slot-option {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 2px 9px;
      background: #fff;
      border: 1px solid;
      border-radius: 14px;
      font-size: 12px;
      line-height: 18px;
      white-space: nowrap;
      cursor: pointer;
      transition: background-color .15s, color .15s;

      &:focus {
        outline: none;
      }

      &:focus-visible {
        box-shadow: 0 0 0 2px rgba($ls-action, .35);
      }
    }

    @each $type, $color in $book-appointment-type-colors {
      .type-color-#{$type} {
        color: $color;
      }

      .type-background-#{$type} {
        background: $color;
        color: #fff;
      }

      .slot-option-#{$type} {
        border-color: rgba($color, .45);
        color: $color;

        &:hover {
          background: rgba($color, .1);
        }

        &.is-selected {
          background: $color;
          border-color: $color;
          color: #fff;
        }
      }
    }

    // Selection

    .selection-bar {
      position: sticky;
      bottom: 12px;
      z-index: 10;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 12px 20px;
      margin-top: 24px;
      padding: 12px 16px;
      background: $book-appointment-heading-color;
      border-radius: $book-appointment-radius;
      color: #fff;
      animation: book-appointment-reveal .2s ease-out;
    }

    .selection-summary {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }

    .selection-icon {
      display: inline-flex;
      flex: 0 0 36px;
      align-items: center;
      justify-content: center;
      height: 36px;
      border-radius: 50%;
      font-size: 16px;
    }

    .selection-title {
      font-weight: 700;
    }

    .selection-subtitle {
      font-size: 13px;
      opacity: .8;
    }

    .selection-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;

      .btn-link {
        color: #fff;
        opacity: .8;

        &:hover,
        &:focus {
          color: #fff;
          opacity: 1;
          text-decoration: none;
        }
      }
    }

    // Confirmation

    .confirmation-card {
      max-width: 680px;
      margin: 0 auto 16px;
      padding: 24px 28px;
    }

    .confirmation-header {
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 18px;

      > .fa {
        font-size: 40px;
        color: map-get($book-appointment-type-colors, 'phone-call');
      }

      h2 {
        margin: 0;
        font-size: 22px;
        color: $book-appointment-heading-color;
      }

      p {
        margin: 2px 0 0;
        color: $book-appointment-heading-color;
      }
    }

    .expiry-warning {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 18px;
      padding: 10px 14px;
      background: #fff5f5;
      border: 1px solid rgba($ls-highlight-color, .5);
      border-radius: $book-appointment-radius;
      color: darken($ls-highlight-color, 30%);
    }

    .appointment-id {
      margin-bottom: 18px;
    }

    .appointment-id-value {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      margin-top: 4px;

      code {
        padding: 6px 10px;
        background: $ls-background-light-color;
        border: 1px solid $book-appointment-border-color;
        border-radius: 4px;
        font-size: 14px;
        word-break: break-all;
        color: $book-appointment-heading-color;
      }
    }

    .appointment-details {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 14px 24px;
      margin: 0 0 22px;
      padding-top: 18px;
      border-top: 1px solid $book-appointment-border-color;

      dt {
        @include book-appointment-label;
      }

      dd {
        margin-top: 2px;
        word-break: break-word;
        color: $book-appointment-heading-color;
      }
    }

    .confirmation-actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .cancel-button {
      color: $ls-cancel-highlight;

      &:hover,
      &:focus {
        color: darken($ls-cancel-highlight, 15%);
      }
    }

    @media (max-width: 767px) {
      .campaign-body {
        padding-left: 18px;
      }

      .slot-day {
        flex-direction: column;
        gap: 6px;
      }

      .slot-day-label {
        display: flex;
        flex: none;
        align-items: baseline;
        gap: 6px;
        padding-top: 0;
      }

      .confirmation-card {
        padding: 18px;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// BookAppointment.vue?7d210bd0